import type { AIWorker } from '../aides/aiworkerTypes';
import type { PronunciationDictionaryConfig } from './voiceAgentTypes';

export function isVoiceAgentAiWorker(aiWorker: AIWorker): boolean {
  return String(aiWorker.sub_type ?? '').trim().toLowerCase() === 'voice agent';
}

export function getVoiceAgentIdFromAiWorker(aiWorker: AIWorker): string | null {
  const id = aiWorker.aiworkers_attrs?.find((attr) => attr.attr_name === 'voice_agent_id')?.attr_value;
  const trimmed = String(id ?? '').trim();
  return trimmed || null;
}

export function normalizeAiWorkersList(response: unknown): AIWorker[] {
  if (Array.isArray(response)) return response as AIWorker[];
  if (response && typeof response === 'object') {
    const agents = (response as { agents?: unknown }).agents;
    if (Array.isArray(agents)) return agents as AIWorker[];
  }
  return [];
}

/** Normalize API `dictionary_id` (string or string[]) to a trimmed string array. */
export function normalizePronunciationDictionaryIds(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw
      .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      .map((id) => id.trim());
  }
  if (typeof raw === 'string' && raw.trim()) {
    return [raw.trim()];
  }
  return [];
}

export function parsePronunciationDictionaryConfig(
  agent: Record<string, unknown> | undefined | null
): PronunciationDictionaryConfig {
  const raw = agent?.pronunciation ?? agent?.pronunciation_dictionary;
  if (!raw || typeof raw !== 'object') {
    return { enabled: false, dictionary_id: null };
  }
  const config = raw as Record<string, unknown>;
  const ids = normalizePronunciationDictionaryIds(config.dictionary_id);
  return {
    enabled: config.enabled === true || ids.length > 0,
    dictionary_id: ids.length > 0 ? ids : null,
  };
}

export function isDictionaryAttachedToVoiceAgent(
  agent: Record<string, unknown> | undefined | null,
  dictionaryId: string
): boolean {
  const config = parsePronunciationDictionaryConfig(agent);
  if (!config.enabled) return false;
  const target = dictionaryId.trim();
  return normalizePronunciationDictionaryIds(config.dictionary_id).includes(target);
}

export function findAiWorkersUsingDictionary(
  dictionaryId: string,
  aiWorkers: AIWorker[],
  voiceAgents: Array<Record<string, unknown>>
): AIWorker[] {
  const target = dictionaryId.trim();
  if (!target) return [];

  const voiceAgentById = new Map<string, Record<string, unknown>>();
  for (const voiceAgent of voiceAgents) {
    const id = String(voiceAgent.id ?? voiceAgent.assistant_id ?? '').trim();
    if (id) voiceAgentById.set(id, voiceAgent);
  }

  return aiWorkers.filter((worker) => {
    if (!isVoiceAgentAiWorker(worker)) return false;
    const voiceAgentId = getVoiceAgentIdFromAiWorker(worker);
    if (!voiceAgentId) return false;
    const voiceAgent = voiceAgentById.get(voiceAgentId);
    return voiceAgent ? isDictionaryAttachedToVoiceAgent(voiceAgent, target) : false;
  });
}

/** PATCH body for voice-agent pronunciation dictionary attach (pronunciation object only). */
export function buildPronunciationDictionaryPatchBody(
  config: PronunciationDictionaryConfig | undefined
): { pronunciation: PronunciationDictionaryConfig } {
  return {
    pronunciation:
      sanitizePronunciationDictionaryForApi(config) ?? {
        enabled: false,
        dictionary_id: null,
      },
  };
}

export function sanitizePronunciationDictionaryForApi(
  config: PronunciationDictionaryConfig | undefined
): PronunciationDictionaryConfig | undefined {
  if (!config) return undefined;
  const dictionaryIds = normalizePronunciationDictionaryIds(config.dictionary_id);
  if (dictionaryIds.length === 0) {
    return { enabled: false, dictionary_id: null };
  }
  return { enabled: true, dictionary_id: dictionaryIds };
}

export function arePronunciationDictionaryConfigsEqual(
  a: PronunciationDictionaryConfig | undefined,
  b: PronunciationDictionaryConfig | undefined
): boolean {
  const left = sanitizePronunciationDictionaryForApi(a) ?? { enabled: false, dictionary_id: null };
  const right = sanitizePronunciationDictionaryForApi(b) ?? { enabled: false, dictionary_id: null };
  return JSON.stringify(left) === JSON.stringify(right);
}
